import { TypeDto } from 'app/api/apiClient/generated/types';
import { getSessionCurrencyTypeId } from 'app/api/cookie';
import { useGetBankAccountTypes } from 'app/api/hooks/core/v1/payee/bankAccounts';
import { useGetRelationships } from 'app/api/hooks/core/v1/payee/payeeRelationships';
import { filterMap, indexBy } from 'app/utils/lib/array';

interface BankAccountCurrencyType extends TypeDto {
  isAvailable: boolean;
}

export const useHasMultiCurrency = (currencyTypeId?: number) => {
  const {
    data: relationshipData,
    isInitialLoading: isLoadingRelationshipData,
  } = useGetRelationships();
  const {
    data: bankAccountTypeData,
    isInitialLoading: isLoadingBankAccountTypeData,
  } = useGetBankAccountTypes();

  const sessionCurrencyTypeAsString = getSessionCurrencyTypeId();
  const sessionCurrencyTypeId = sessionCurrencyTypeAsString ? Number(sessionCurrencyTypeAsString) : null;
  const currencyTypeIdFilter = currencyTypeId ?? sessionCurrencyTypeId;

  const bankAccountTypes = (bankAccountTypeData?.bankAccountTypes ?? []).filter(type =>
    currencyTypeIdFilter ? type.currencyType.id === currencyTypeIdFilter : true);
  const bankAccountTypeIds = bankAccountTypes.map(x => x.bankAccountType.id);
  const availableCurrencyTypes = indexBy(bankAccountTypes, type => type.currencyType.id.toString());

  // get list of unique currency types from relationshipData
  // eslint-disable-next-line @stylistic/max-len, @typescript-eslint/no-non-null-assertion
  const relationshipCurrencyTypes = filterMap(relationshipData?.payeeRelationships ?? [], rel => !!rel.payor.currencyType, rel => rel.payor.currencyType!);
  const relationshipCurrencyTypeLookup = indexBy(relationshipCurrencyTypes, type => type.id.toString());
  const uniqueRelationshipCurrencyTypes = Object.values(relationshipCurrencyTypeLookup).sort((a, b) => a.id - b.id);

  const payeeAvailableCurrencyTypes = uniqueRelationshipCurrencyTypes.map<BankAccountCurrencyType>(type => ({
    ...type,
    isAvailable: !!availableCurrencyTypes[type.id.toString()],
  }));

  return {
    bankAccountTypeIds,
    bankAccountTypes,
    hasMultiCurrency: payeeAvailableCurrencyTypes.length > 1,
    isInitialLoading: isLoadingRelationshipData || isLoadingBankAccountTypeData,
    /**
     * List of currency types available to the payee based on the currency types
     * of payors for claimed relationships
     */
    payeeAvailableCurrencyTypes,
    sessionCurrencyTypeId,
  };
};
